import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { history } from '../store';

// components
import EventFeedCard from './EventFeedCard';

// actions
import { deleteEvent } from '../actions/cardEvents';

// functions
import { shouldRenderCard } from '../services/eventFeedService';
import { mapBasketToUserEvent, createFakeEvent, orderByCreated } from '../services/eventService';
import { gtmPageLoadEvent } from '../services/gtmService';

/**
 *
 * Acts as main content of page, vertical feed of cards
 * component orchestrates the behavior deciding wether each card renders
 * and in what order.
 *
 * @param { string/jsx } emptyFeedContent content to display if no other child components are availible.
 * @param { Array<Objects> } events
 * @param { Object } cardTemplates
 * @param { Array<Objects> } baskets
 * @param { function } deleteEvent
 * @param {Object} location react router prop with current location info
 *
 */
export class _EventFeed extends React.Component {

  componentDidMount() {
    this.registerWithGtmAsPageLoad();

    if(history.location.pathname !== '/basket') {
      this.unlistenHistory = history.listen((location) => {
        if(location.pathname !== '/basket') {
          this.registerWithGtmAsPageLoad();
        }
      });
    }
  }

  componentWillUnmount() {
    if(this.unlistenHistory) this.unlistenHistory();
  }

  registerWithGtmAsPageLoad() {
    gtmPageLoadEvent(history.location.pathname);
  }

  render() {
    const {
      emptyFeedContent, // eventFeed prop
      events, cardTemplates, baskets, // store values
      deleteEvent, // actions
      location // router props
    } = this.props;

    /**
     * pass event/card data to service to decide wether or not to render
     * then assemble event feed card
     */
    function shouldRender(acc, userEvent) {
      return shouldRenderCard(userEvent, cardTemplates, 'eventFeed', location) ?
        acc.concat(<EventFeedCard
          userEvent={userEvent}
          removeEvent={deleteEvent}
          key={userEvent.id}
        />, []) : acc;
    }

    /**
     * safely extract atTopImportance overide information from umbraco card template
     * TODO:
     *   make pure
     */
    function getImportance(event) {
      const template = cardTemplates[event.template] &&
        cardTemplates[event.template][event.cardName] &&
        cardTemplates[event.template][event.cardName].atTopImportance &&
        parseInt(cardTemplates[event.template][event.cardName].atTopImportance, 10);
      return template || false;
    }

    /**
     * move cms/template overwridden card order cards to top.
     */
    function raiseOverriddenEvents(events) {
      const reOrdered = events.reduce((acc, event) => {
        const importance = getImportance(event);
        if (importance) {
          return { ...acc, topEvents: [...acc.topEvents, { event, importance }] };
        }
        return { ...acc, rest: [...acc.rest, event] };
      }, { topEvents: [], rest: [] });

      return [
        ...reOrdered.topEvents.sort((a, b) => a.importance - b.importance)
          .map(event => event.event),
        ...reOrdered.rest
      ];
    }

    /* wait on all required data */
    if (!cardTemplates || !events || cardTemplates.length === 0) {
      return <div className="loader page-loader" />;
    }

    /* Add default cards */
    let defaultEvents = [];

    // Basket cards
    if (baskets) {
      defaultEvents = defaultEvents.concat(
        baskets.filter(basket =>
          basket.entries &&
          basket.entries !== null &&
          basket.entries.length))
        .map(mapBasketToUserEvent);
    }

    // recently viewed and favourites
    defaultEvents = defaultEvents.concat([
      createFakeEvent('RecentlyViewedCard', 'ActivitySliderCard'),
      createFakeEvent('FavouritesCard', 'ActivitySliderCard')
    ]);

    // assemble and order cards to render
    let cardsToRender = orderByCreated([...events, ...defaultEvents]);
    // cards with topImportance re-ordered above all else
    cardsToRender = raiseOverriddenEvents(cardsToRender)
      .reduce(shouldRender, []);

    return (
      <div className="c-event-feed">
        {cardsToRender.length ?
          cardsToRender : (emptyFeedContent || null)}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  events: state.eventFeed.events,
  cardTemplates: state.eventFeed.cardTemplates,
  baskets: state.baskets.baskets
});

const mapDispatchToProps = dispatch => bindActionCreators({
  deleteEvent
}, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(_EventFeed));



// WEBPACK FOOTER //
// ./src/layout/EventFeed.js